// src/components/Chat/ChatModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, User, X, Send } from 'lucide-react';
import { useChat } from '../../hooks/useChat';

interface UserType {
  id: string;
  username: string;
}

interface ChatModalProps {
  currentUserId: string;
  targetUserId?: string;
  targetUserName?: string;
  roomId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ChatModal: React.FC<ChatModalProps> = ({
  currentUserId,
  targetUserId: initialTargetUserId,
  targetUserName: initialTargetUserName,
  roomId: customRoomId,
  isOpen,
  onClose,
}) => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [activeTargetUser, setActiveTargetUser] = useState<UserType | null>(null);
  const [fetchedRoomId, setFetchedRoomId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 1. 決定最終使用的 activeRoomId（優先使用外部傳入的 customRoomId）
  const activeRoomId = customRoomId || fetchedRoomId;

  // 2. 初始化選擇傳入的對象
	useEffect(() => {
		if (!activeTargetUser || !currentUserId || customRoomId) return;

		const fetchOrCreateRoom = async () => {
			try {
				const res = await fetch('/api/chat/rooms', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-User-Id': currentUserId,
					},
					body: JSON.stringify({ 
						userId: currentUserId, // 🔑 明確傳入 userId
						targetUserId: activeTargetUser.id 
					}),
				});

				if (!res.ok) {
					const errorData = await res.json().catch(() => ({}));
					console.error('取得房間失敗:', res.status, errorData);
					return;
				}

				const data = await res.json();
				if (data.roomId) {
					setFetchedRoomId(data.roomId);
				}
			} catch (err) {
				console.error('網路或請求異常:', err);
			}
		};

		fetchOrCreateRoom();
	}, [activeTargetUser, currentUserId, customRoomId]);

  // 3. 當 activeTargetUser 改變且沒有 customRoomId 時，自動向後端獲取/建立 Room ID
  useEffect(() => {
    if (!activeTargetUser || !currentUserId || customRoomId) return;

    const fetchOrCreateRoom = async () => {
      try {
        const res = await fetch('/api/chat/rooms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': currentUserId,
          },
          body: JSON.stringify({ targetUserId: activeTargetUser.id }),
        });
        const data = await res.json();
        if (data.roomId) {
          setFetchedRoomId(data.roomId);
        }
      } catch (err) {
        console.error('取得房間失敗:', err);
      }
    };

    fetchOrCreateRoom();
  }, [activeTargetUser, currentUserId, customRoomId]);

  // 4. 取得使用者清單
  useEffect(() => {
    if (!isOpen || !currentUserId) return;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/users/search?q=*', {
          headers: {
            'X-User-Id': currentUserId,
          },
        });

        const contentType = res.headers.get('content-type');

        if (res.ok && contentType && contentType.includes('application/json')) {
          const data = await res.json();
          setUsers(data.users || []);
        } else {
          console.warn('API 回應非 JSON 格式:', res.status);
        }
      } catch (err) {
        console.error('抓取使用者清單失敗:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen, currentUserId]);

  const handleSelectUser = (user: UserType) => {
    setActiveTargetUser(user);
  };

  // 🔑 解構 useChat Hook 補上 isConnected、messages、sendMessage 與 markAsRead
  const { messages, isConnected, sendMessage, markAsRead } = useChat(
    currentUserId,
    activeRoomId
  );

  // 🔑 當開啟聊天視窗且選定房間時，向後端發送「標記已讀」
  useEffect(() => {
    if (!isOpen || !currentUserId || !activeRoomId) return;

    markAsRead();
  }, [isOpen, currentUserId, activeRoomId, markAsRead]);

  // 新訊息載入時自動滾動至最底部
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (!isOpen) return null;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    await sendMessage(inputText.trim());
    setInputText('');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 text-white w-full max-w-2xl h-[600px] rounded-2xl flex overflow-hidden shadow-2xl border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 左側：使用者列表 */}
        <div className="w-1/3 border-r border-slate-700 flex flex-col">
          <div className="p-4 font-bold text-lg border-b border-slate-700 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-400" />
            聊天對象
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-xs text-slate-400 text-center">載入使用者中...</div>
            ) : users.length === 0 ? (
              <div className="p-4 text-xs text-slate-400 text-center">目前沒有其他使用者</div>
            ) : (
              users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className={`w-full p-3 text-left hover:bg-slate-700/50 transition flex items-center gap-2 border-b border-slate-700/50 ${
                    activeTargetUser?.id === user.id ? 'bg-indigo-600/20 border-l-4 border-l-indigo-500' : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-indigo-300" />
                  </div>
                  <span className="font-semibold text-sm truncate">{user.username}</span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* 右側：對話視窗 */}
        <div className="flex-1 flex flex-col">
          {/* 頂部列 */}
          <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-800/80">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm">
                {activeTargetUser ? activeTargetUser.username : '選擇聊天對象'}
              </span>
              {activeRoomId && (
                <span className="text-xs">
                  {isConnected ? (
                    <span className="text-green-400">● 在線</span>
                  ) : (
                    <span className="text-red-400">○ 斷線</span>
                  )}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {activeRoomId ? (
            <>
              {/* 訊息紀錄 */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center text-xs text-slate-500 mt-4">
                    還沒有訊息，發個打招呼訊息吧！
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.sender_id === currentUserId;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] p-3 rounded-2xl text-sm ${
                            isMe
                              ? 'bg-indigo-600 text-white rounded-br-none'
                              : 'bg-slate-700 text-slate-200 rounded-bl-none'
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* 輸入框 */}
              <form onSubmit={handleSend} className="p-3 border-t border-slate-700 flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="輸入訊息..."
                  className="flex-1 bg-slate-900 text-white rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-500 p-2 rounded-xl transition"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500 text-sm p-4">
              <MessageSquare className="w-12 h-12 mb-2 text-slate-600" />
              點擊左側使用者開始對話
            </div>
          )}
        </div>
      </div>
    </div>
  );
};